
import { ApiPagedResponse, PaginationInfo } from "app/client-core/api-response-models";
import { StringAnyMap } from "app/core/api/interfaces/keyvalue-maps.interface";
import { AppDatatableNavigationOptions } from "./navigation-options.model";
import { AppDatatableTreeStatus } from "app/shared/datatable/enums";

export class AppDatatableModel<T> {

    readonly DEFAULT_PAGE_SIZE: number = 20;
    readonly DEFAULT_PAGE_INDEX: number = 0;
    readonly DEFAULT_SORT_DIR: string = "asc";

    public versionId: string;
    public idProp: string;
    public treeLayout: boolean;
    public pagedResponse: ApiPagedResponse<T>;
    public navigationOptions: AppDatatableNavigationOptions;

    private _paginationInfo: PaginationInfo;

    constructor(pagedResponse: ApiPagedResponse<T> = undefined) {
        this.versionId = Math.random().toString(36).substr(2, 9);
        this.treeLayout = false;
        this.navigationOptions = new AppDatatableNavigationOptions();
        this.pagedResponse = pagedResponse;
    }

    public clone(stripResponse: boolean = false, stripChangeAction: boolean = false, stripChangeEvent: boolean = false) : AppDatatableModel<T> {
        let clone: AppDatatableModel<T>;
        if(stripResponse) {
            clone = new AppDatatableModel<T>(undefined);
        } else {
            clone = new AppDatatableModel<T>(this.pagedResponse);
        }    
        clone.idProp = this.idProp;
        clone.treeLayout = this.treeLayout;
        clone.paginationInfo = JSON.parse(JSON.stringify(this.paginationInfo));
        clone.navigationOptions = this.navigationOptions;
        if(stripChangeAction) {
            clone.navigationOptions.changeAction = undefined;
        }
        if(stripChangeEvent) {
            clone.navigationOptions.changeEvent = undefined;
        }
        return clone;
    }

    //TODO: generics issue
    get items(): Array<any> {
        if(this.pagedResponse) {
            return this.pagedResponse.items;
        } else {
            return [];
        };
    }

    set items(items: Array<any>) {
        this.pagedResponse.items = items;
    }

    get paginationInfo(): PaginationInfo {
        if(this._paginationInfo) {
            return this._paginationInfo;
        } else if(this.pagedResponse) {
            let r = this.pagedResponse;
            this._paginationInfo = new PaginationInfo (
                r.pageIndex, r.pageSize, r.totalCount, r.totalPages, r.hasPreviousPage, r.hasNextPage
            );
            return this._paginationInfo;
        } else {
            return new PaginationInfo(this.DEFAULT_PAGE_INDEX, this.DEFAULT_PAGE_SIZE);
        };
    }

    set paginationInfo(paginationInfo: PaginationInfo) {
        if(!this._paginationInfo) {
            this._paginationInfo = new PaginationInfo();
            this._paginationInfo.pageIndex = paginationInfo.pageIndex;
            this._paginationInfo.pageSize = paginationInfo.pageSize;
            this._paginationInfo.totalCount = paginationInfo.totalCount;
            this._paginationInfo.totalPages = paginationInfo.totalPages;
            this._paginationInfo.hasPreviousPage = paginationInfo.hasPreviousPage;
            this._paginationInfo.hasNextPage = paginationInfo.hasNextPage;
        }
    }

    get queryParameters(): any {
        let qParams = {
            pageIndex: this.paginationInfo.pageIndex,
            pageSize: this.paginationInfo.pageSize
        };
        if(this.navigationOptions.sortBy && this.navigationOptions.sortBy.trim().length>0) {
            qParams['sortBy'] = this.navigationOptions.sortBy;
            if(this.navigationOptions.sortDirection) {
                qParams['sortDirection'] = this.navigationOptions.sortDirection;
            } else {
                qParams['sortDirection'] = this.DEFAULT_SORT_DIR;
            }
        }
        if(this.navigationOptions.searchString && this.navigationOptions.searchString.trim().length>0) {
            qParams['search'] = this.navigationOptions.searchString;
        }
        // mix-in calling component options
        if(this.navigationOptions.queryOptions) {
            this.navigationOptions.queryOptions.forEach((v,k) => {
                qParams[k] = v;
            });
        }
        return qParams;
    }

    public setFrom(pagedResponse: ApiPagedResponse<T>, expandAllOnInit: boolean = false) : void {
        this.paginationInfo = new PaginationInfo (
                pagedResponse.pageIndex, 
                pagedResponse.pageSize, 
                pagedResponse.totalCount, 
                pagedResponse.totalPages, 
                pagedResponse.hasPreviousPage, 
                pagedResponse.hasNextPage
            );
        if(!this.treeLayout) {
            this.pagedResponse = pagedResponse;
        } else {
            //TODO: need generics with properties
            let anyResponse: any = pagedResponse;
            for (let r of anyResponse.items) {
                if (r.childrenCount && r.childrenCount > 0) {
                    r.treeStatus = expandAllOnInit ? AppDatatableTreeStatus.EXPANDED : AppDatatableTreeStatus.COLLAPSED;
                } else {
                    r.treeStatus = AppDatatableTreeStatus.DISABLED;
                }
            }
            this.pagedResponse = pagedResponse;
            //set pagination info for tree view
            this.paginationInfo.totalCount = this.items.length;
            this.paginationInfo.pageSize = this.items.length;
        }
    }

    public appendFrom(pagedResponse: ApiPagedResponse<T>, expandAllOnInit: boolean = false) : void {
        
        if(!this.treeLayout) {
            this.pagedResponse.items = [...this.pagedResponse.items, ...pagedResponse.items];
        } else {
            //TODO: need generics with properties
            let anyResponse: any = pagedResponse;
            for (let r of anyResponse.items) {
                if (r.childrenCount && r.childrenCount > 0) {
                    r.treeStatus = expandAllOnInit ? AppDatatableTreeStatus.EXPANDED : AppDatatableTreeStatus.COLLAPSED;
                } else {
                    r.treeStatus = AppDatatableTreeStatus.DISABLED;
                }
            }
            this.pagedResponse.items = [...this.pagedResponse.items, ...pagedResponse.items];
            //set pagination info for tree view
            this.paginationInfo.totalCount = this.items.length;
            this.paginationInfo.pageSize = this.items.length;
        }
    }

    public getRowIndex(id: string): number {
        let rowIndex = undefined;
        if(this.items) {
            rowIndex = this.items.findIndex(
                r => id === r[this.idProp]
            );
        }
        return rowIndex;
    }
}



// WEBPACK FOOTER //
// ./src/app/shared/datatable/models/base.model.ts